import { SelectOption } from '@chronosphereio/chrono-ui';
import {
  type DurationString,
  formatWithTimeZone,
  getDurationFromTimeRangeStart,
  getProductName,
  getTimeRangeFromDurationString,
  isTimeRangeInputValueAbsolute,
  normalizeTimeZone,
  TIME_RANGE_INVALID_ORDER_MESSAGE,
  TimeFormat,
  TimeRangeInputValue,
} from '@chronosphereio/core';
import { sub } from 'date-fns';
import { getMonthTimeRange, NamedTimeOption } from '@/core';
import { formatRelativeRange, formatTimeRangeInputValue, parseTimeRange } from './format';

export interface TimeOption {
  value: TimeRangeInputValue;
  display?: string;
}

export const DEFAULT_TIME_RANGE_OPTIONS: TimeOption[] = [
  { value: getTimeRangeFromDurationString('5m'), display: 'Last 5 minutes' },
  { value: getTimeRangeFromDurationString('15m'), display: 'Last 15 minutes' },
  { value: getTimeRangeFromDurationString('30m'), display: 'Last 30 minutes' },
  { value: getTimeRangeFromDurationString('1h'), display: 'Last 1 hour' },
  { value: getTimeRangeFromDurationString('3h'), display: 'Last 3 hours' },
  { value: getTimeRangeFromDurationString('6h'), display: 'Last 6 hours' },
  { value: getTimeRangeFromDurationString('12h'), display: 'Last 12 hours' },
  { value: getTimeRangeFromDurationString('1d'), display: 'Last 1 day' },
  { value: getTimeRangeFromDurationString('2d'), display: 'Last 2 days' },
  { value: getTimeRangeFromDurationString('5d'), display: 'Last 5 days' },
  { value: getTimeRangeFromDurationString('7d'), display: 'Last 7 days' },
  { value: getTimeRangeFromDurationString('14d'), display: 'Last 14 days' },
];

export const DEFAULT_TIME_RANGE_OPTIONS_WITH_FEATURE_FLAG: TimeOption[] = [
  { value: getTimeRangeFromDurationString('5m'), display: 'Last 5 minutes' },
  { value: getTimeRangeFromDurationString('15m'), display: 'Last 15 minutes' },
  { value: getTimeRangeFromDurationString('30m'), display: 'Last 30 minutes' },
  { value: getTimeRangeFromDurationString('1h'), display: 'Last 1 hour' },
  { value: getTimeRangeFromDurationString('3h'), display: 'Last 3 hours' },
  { value: getTimeRangeFromDurationString('6h'), display: 'Last 6 hours' },
  { value: getTimeRangeFromDurationString('12h'), display: 'Last 12 hours' },
  { value: getTimeRangeFromDurationString('24h'), display: 'Last 24 hours' },
  { value: getTimeRangeFromDurationString('2d'), display: 'Last 2 days' },
  { value: getTimeRangeFromDurationString('5d'), display: 'Last 5 days' },
  { value: getTimeRangeFromDurationString('7d'), display: 'Last 7 days' },
  { value: getTimeRangeFromDurationString('14d'), display: 'Last 14 days' },
  { value: getTimeRangeFromDurationString('30d'), display: 'Last 30 days' },
  { value: getTimeRangeFromDurationString('6M'), display: 'Last 6 months' },
  { value: getTimeRangeFromDurationString('1y'), display: 'Last 1 year' },
];

export const CUSTOM_OPTION_KEY = 'custom' as const;
export const ERROR_OPTION_KEY = 'error' as const;

export interface TimeRangeSelectOption extends SelectOption {
  value: typeof CUSTOM_OPTION_KEY | typeof ERROR_OPTION_KEY | TimeRangeInputValue;
}

const CUSTOM_TIME_RANGE_LABEL = 'Custom time range';

// Programmatically calculating the example dates, so the error message automatically matches if
// we adjust our formats in the future.
const EXAMPLE_ABSOLUTE_STR_12HR = formatTimeRangeInputValue({
  timeRange: {
    start: new Date('2025-01-01T00:00:00'),
    end: new Date('2025-01-02T23:59:59'),
  },
  timeZone: 'local',
  timeFormat: '12h',
});
const EXAMPLE_ABSOLUTE_STR_24HR = formatTimeRangeInputValue({
  timeRange: {
    start: new Date('2025-01-01T00:00:00'),
    end: new Date('2025-01-02T23:59:59'),
  },
  timeZone: 'local',
  timeFormat: '24h',
});

export function getErrorOptionLabels(panwRebrandStubYear?: boolean) {
  const productName = getProductName(panwRebrandStubYear);
  return {
    UNSUPPORTED_12H: `Format not supported. For absolute ranges, both ISO and ${productName}'s format are supported (e.g. ${EXAMPLE_ABSOLUTE_STR_12HR}). For relative ranges, a variety of formats are supported (e.g. Last 1 hour, past 1 hour, 1 hour, 1hr, 1h).`,
    UNSUPPORTED_24H: `Format not supported. For absolute ranges, both ISO and ${productName}'s format are supported (e.g. ${EXAMPLE_ABSOLUTE_STR_24HR}). For relative ranges, a variety of formats are supported (e.g. Last 1 hour, past 1 hour, 1 hour, 1hr, 1h).`,
    INVALID_ORDER: TIME_RANGE_INVALID_ORDER_MESSAGE,
  };
}

export const ERROR_OPTION_LABELS = getErrorOptionLabels();
export type ErrorOptionKey = keyof typeof ERROR_OPTION_LABELS;

/**
 * Filters time range options using the following logic:
 * - Matches any items that include the input string without case sensitivity.
 * - If the input parses to a relative time range, also matches options with the same pastDuration.
 * - When `includeSyntheticOptions` is true, prepends a synthetic option for parsed relative
 *   ranges not already in the list. This must NOT be enabled when used as MUI Autocomplete's
 *   `filterOptions` prop, which expects a subset of the original options array.
 * - Always shows the custom option unless feature flag is enabled.
 */
export function filterTimeRangeOptions(
  options: TimeRangeSelectOption[],
  { inputValue, includeSyntheticOptions }: { inputValue: string; includeSyntheticOptions?: boolean }
) {
  const trimmedInput = inputValue.toLowerCase().trim();

  // Try parsing the input as a time range to enable shorthand matching (e.g. "5d" → "Last 5 days")
  const parsedRange = trimmedInput ? parseTimeRange(inputValue) : undefined;
  const parsedDuration = parsedRange ? getDurationFromTimeRangeStart(parsedRange) : undefined;

  const filtered = options.filter((option) => {
    if (option.key === CUSTOM_OPTION_KEY || option.key === ERROR_OPTION_KEY) {
      return true;
    }

    if (option.label.toLowerCase().indexOf(trimmedInput) >= 0) {
      return true;
    }

    if (parsedDuration && typeof option.value === 'object' && !isTimeRangeInputValueAbsolute(option.value)) {
      return getDurationFromTimeRangeStart(option.value) === parsedDuration;
    }

    return false;
  });

  // If the input parses to a relative range not already represented in the filtered list,
  // prepend a synthetic option so the user can select it.
  if (includeSyntheticOptions && parsedDuration) {
    const alreadyPresent = filtered.some(
      (opt) => typeof opt.value === 'object' && getDurationFromTimeRangeStart(opt.value) === parsedDuration
    );
    if (!alreadyPresent) {
      filtered.unshift({
        key: `custom-relative-${parsedDuration}`,
        label: formatRelativeRange(parsedDuration as DurationString),
        value: getTimeRangeFromDurationString(parsedDuration),
      });
    }
  }

  return filtered;
}

const getTimeRangeSelectOptionDisplayValue = (timeOption: TimeOption, timeFormat: TimeFormat, timeZone?: string) => {
  return timeOption.display ?? formatTimeRangeInputValue({ timeRange: timeOption.value, timeZone, timeFormat });
};

/**
 * Normalizes time options, which can be an array of options or a named string into an array of options.
 */
function normalizeTimeRangeOptions(
  timeOptions: TimeOption[] | NamedTimeOption,
  timeZone?: string,
  enableNewDateTimePickerFormat?: boolean
): TimeOption[] {
  if (timeOptions === 'default') {
    return enableNewDateTimePickerFormat ? DEFAULT_TIME_RANGE_OPTIONS_WITH_FEATURE_FLAG : DEFAULT_TIME_RANGE_OPTIONS;
  }

  if (timeOptions === 'monthly') {
    return generateMonthlyTimeOptions(timeZone);
  }

  return timeOptions;
}

/**
 * Takes the input time options and translates them into a format that can be used with a `Select`.
 */
export function getTimeRangeSelectOptions({
  initTimeOptions,
  timeFormat,
  timeZone,
  includeCustomOption = true,
  enableNewDateTimePickerFormat = false,
}: {
  initTimeOptions: TimeOption[] | NamedTimeOption;
  timeFormat: TimeFormat;
  timeZone?: string;
  includeCustomOption?: boolean;
  enableNewDateTimePickerFormat?: boolean;
}) {
  // We track the longest display value in the options, so that we can calculate how wide it is in
  // pixels to determine the width we use for the popper.
  let longestDisplayValue = includeCustomOption ? CUSTOM_TIME_RANGE_LABEL : '';

  const CUSTOM_INPUT_OPTION: TimeRangeSelectOption = {
    key: CUSTOM_OPTION_KEY,
    label: CUSTOM_TIME_RANGE_LABEL,
    value: CUSTOM_OPTION_KEY,
  };

  const timeOptions: TimeRangeSelectOption[] = [
    ...normalizeTimeRangeOptions(initTimeOptions, timeZone, enableNewDateTimePickerFormat).map((item) => {
      const displayValue = getTimeRangeSelectOptionDisplayValue(item, timeFormat, timeZone);

      if (displayValue.length > longestDisplayValue.length) {
        longestDisplayValue = displayValue;
      }

      return {
        key: displayValue,
        value: item.value,
        label: displayValue,
      };
    }),
    ...(includeCustomOption ? [CUSTOM_INPUT_OPTION] : []),
  ];

  return {
    timeOptions,
    longestDisplayValue,
  };
}

/**
 * Generates an array of absolute time options that represent the start/end of
 * the last 12 months in the specified time zone (or local time if not specified).
 */
export function generateMonthlyTimeOptions(timeZone?: string): TimeOption[] {
  const monthCount = 12;
  const monthFormat = 'MMMM yyyy';

  const normalizedTimeZone = normalizeTimeZone(timeZone);

  // Grab current month.
  const currentMonthValue = getMonthTimeRange(undefined, normalizedTimeZone);
  const startOfNow = new Date(currentMonthValue.start);

  const timeOptions: TimeOption[] = [
    {
      value: currentMonthValue,
      display: formatWithTimeZone(startOfNow, monthFormat, normalizedTimeZone),
    },
  ];

  // Iterate back through remaining months.
  for (let i = 1; i < monthCount; i++) {
    let result: Date;

    if (normalizedTimeZone === 'UTC') {
      const year = startOfNow.getUTCFullYear();
      const month = startOfNow.getUTCMonth();

      // Handle year rollover
      // Example: January 2025 - 5 months = August 2024
      const totalMonths = year * 12 + month - i;
      const prevYear = Math.floor(totalMonths / 12);
      const prevMonth = totalMonths % 12;

      result = new Date(Date.UTC(prevYear, prevMonth, 1, 0, 0, 0, 0));
    } else {
      result = sub(startOfNow, { months: i });
    }

    const value = getMonthTimeRange(result, normalizedTimeZone);

    timeOptions.push({
      value,
      display: formatWithTimeZone(result, monthFormat, normalizedTimeZone),
    });
  }

  return timeOptions;
}
